/* eslint-disable key-spacing */
/* eslint-disable no-restricted-syntax */
import { useEffect, useState } from "react";

const initStateWithUniqIds = rootNode => {
	let curId = 0;
	const _addId = node => {
		node._id = curId; // eslint-disable-line
		curId += 1;

		const { children } = node;
		if (children) {
			for (const child of children) {
				_addId(child);
			}
		}

		return node;
	};

	return _addId({ ...rootNode });
};

// recursively set status for this node and all children, in place
const setStatusDown = (node, status) => {
	node.checked = status; // eslint-disable-line
	const { children } = node;
	if (children) {
		for (const child of children) {
			setStatusDown(child, status);
		}
	}
	return { ...node };
};

const setAllCheckedStatus = setStatusDown;

const setAllOpenStatus = (node, isOpen) => {
	const newNode = { ...node };
	const { children } = newNode;

	if (children) {
		newNode.isOpen = isOpen;
		newNode.children = children.map(child => setAllOpenStatus(child, isOpen));
	}

	return newNode;
};

const isValidOpenStatus = node => {
	const { children, isOpen } = node;

	if (children && isOpen === undefined) return false; // parent node needs to have 'isOpen'
	if (!children && isOpen !== undefined) return false; // children can't have 'isOpen'

	if (children) {
		for (const child of children) {
			if (!isValidOpenStatus(child)) return false;
		}
	}

	return true;
};

const initializeTreeState = (data, initCheckedStatus = "unchecked", initOpenStatus = "open") => {
	let initState = initStateWithUniqIds(data);

	switch (initCheckedStatus) {
		case "unchecked":
			initState = setAllCheckedStatus(initState, 0);
			break;

		case "checked":
			initState = setAllCheckedStatus(initState, 1);
			break;

		default:
			break;
	}

	switch (initOpenStatus) {
		case "open":
			initState = setAllOpenStatus(initState, true);
			break;

		case "closed":
			initState = setAllOpenStatus(initState, false);
			break;

		default:
			if (!isValidOpenStatus(initState)) {
				console.warn("custom open status is invalid! Fell back to all opened.");
				initState = setAllOpenStatus(initState, true);
			}
	}

	return initState;
};

const getEvent = (eventName, path, ...params) => ({
	type: eventName,
	path,
	params,
});

const findAllTargetPathByProp = (root, propName, targetVal) => {
	const res = [];

	const _traverse = (node, curPath) => {
		if (node[propName] === targetVal) {
			res.push({ ...curPath });
		}

		const { children } = node;
		if (children) {
			children.forEach((n, i) => _traverse(n, [...curPath, i]));
		}
	};

	_traverse(root, []);

	return res;
};

const findTargetPathByProp = (root, propName, targetVal) => {
	const allPaths = findAllTargetPathByProp(root, propName, targetVal);
	return allPaths.length > 0 ? allPaths[0] : null;
};

const getNewCheckStatus = node => {
	const { children } = node;
	if (!children?.length > 0) return node.checked;

	let sum = 0;
	for (const c of children) {
		sum += c.checked;
	}

	let newCheckStatus = 0.5; // some checked
	if (sum === children.length) {
		newCheckStatus = 1; // all checked
	} else if (sum === 0) {
		newCheckStatus = 0; // all unchecked
	}

	return newCheckStatus;
};

const updateStatusUp = nodes => {
	if (nodes.length === 0) return;

	const curNode = nodes.pop();
	curNode.checked = getNewCheckStatus(curNode);

	updateStatusUp(nodes);
};

const checkNode = (rootNode, path, status) => {
	let curNode = rootNode;
	const parentNodes = [curNode]; // parent nodes for getNewCheckStatus() upwards

	for (const idx of path) {
		curNode = curNode.children[idx];
		parentNodes.push(curNode);
	}

	setStatusDown(curNode, status); // update check status of this node and all childrens, in place

	parentNodes.pop(); // don't need to check this node's level
	updateStatusUp(parentNodes); // update check status up, from this nodes parent, in place

	return { ...rootNode };
};

const findTargetNode = (root, path) => {
	let curNode = root;
	for (const idx of path) {
		const { children } = curNode;
		if (idx >= children.length || idx < 0) {
			throw new Error("finding node failed: invalid path!!");
		}
		curNode = children[idx];
	}

	return curNode;
};

const toggleOpen = (rootNode, path, isOpen) => {
	const targetNode = findTargetNode(rootNode, path);

	if (targetNode.children) {
		targetNode.isOpen = isOpen;
	} else {
		throw new Error("only parent node (folder) can be opened!!");
	}

	return { ...rootNode };
};

const useTreeState = ({ data, onChange, options = {}, customReducers = {} }) => {
	const [treeState, setTreeState] = useState(null);
	const [event, setEvent] = useState({
		type: "initialization",
		path: null,
		params: [],
	});

	const { initCheckedStatus, initOpenStatus } = options;
	useEffect(() => {
		const initState = initializeTreeState(data, initCheckedStatus, initOpenStatus);
		setTreeState(initState);
	}, []);

	useEffect(() => {
		if (typeof onChange === "function" && treeState && event) {
			onChange(treeState, event);
		}
	}, [treeState, event]);

	const _setTreeState = state => {
		const e = getEvent("setTreeState", null, state);

		setEvent(e);
		setTreeState(state);
	};

	const getExternalReducer =
		(reducer, name) =>
		(path, ...args) => {
			const _path = path ? [...path] : null;
			const e = getEvent(name, _path, ...args);
			const newState = reducer(treeState, _path, ...args);

			setEvent(e);
			setTreeState(newState);
		};

	const getReducerByProp =
		reducer =>
		(propName, targetValue, ...args) => {
			const path = findTargetPathByProp(treeState, propName, targetValue);
			return path ? reducer(path, ...args) : null;
		};

	const _customReducers = Object.fromEntries(
		Object.entries(customReducers).map(([name, f]) => [name, getExternalReducer(f, name)]),
	);

	const reducers = {
		setTreeState: _setTreeState,
		checkNode: getExternalReducer(checkNode, "checkNode"),
		toggleOpen: getExternalReducer(toggleOpen, "toggleOpen"),
		..._customReducers,
	};

	reducers.checkNodeByProp = getReducerByProp(reducers.checkNode);
	reducers.toggleOpenByProp = getReducerByProp(reducers.toggleOpen);

	return {
		treeState,
		reducers,
	};
};

export default useTreeState;
